import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from '_common/hooks';

import { AuthService } from '_common/services';
import { listObjects } from '_common/components/Table/TableSlice';

import Empty from '_common/modals/UserManagement/UserTokensModal/TokenList/Empty/Empty';

import type { EmptyProps } from '_common/modals/UserManagement/UserTokensModal/TokenList/Empty/Empty';

import styles from './TokenList.module.scss';
import { ThunksTable, Cell, type TableColumn, type TableRow } from '_common/components/Table';
import { FormattedMessage, useIntl } from 'react-intl';
import { dayjs } from 'utils';
import { removeUserActiveSession } from 'Settings/pages/TenantSettingsPage/Users/redux/userManagementSlice';

const IDENTITY: Table.Identity = 'userManagement/tokens';

type TokenListProps = {
  emptyState: { title: EmptyProps['title']['id']; body: EmptyProps['body']['id'] };
  loadingState: { message: string };
  filters: Request.FilterParams;
};

const TokenList = ({ emptyState, loadingState, filters }: TokenListProps) => {
  const dispatch = useDispatch();
  const intl = useIntl();

  const user = useSelector((state) => state.modals.UserTokensModal.user);
  const list = useSelector((state) => state.table.identity['userManagement/tokens'].list);
  const tokens = useSelector((state) => state.userManagement.tokens);
  const selectedTab = useSelector((state) => state.tabMenu.userTokenHistory);

  const handleRemoveSession = (tokenId: string) => {
    dispatch(removeUserActiveSession({ userId: user.id, tokenId }));
  };

  //#region Table object listing handlers
  const fetchObjects = useCallback(
    async (parameters: any) => {
      const response = await new AuthService().getUserTokens(user.id, {
        ...parameters,
        ...filters,
      });

      return {
        ...response,
        data: {
          nodes: response.data,
          dataStorage: 'tenantTokens',
        },
      };
    },
    [filters],
  );

  useEffect(() => {
    dispatch(
      listObjects({
        identity: IDENTITY,
        fetch: fetchObjects,
        cause: 'INITIAL',
        request: { offset: 0 },
      }),
    );
  }, []);

  // No useEffectOnUpdate since fetchObjects doesn't have any dependencies
  // Add if dependencies are added to fetchObjects

  //#endregion

  const renderEmptyState = () => {
    return (
      <div className={`${styles.center} ${styles.empty}`}>
        <Empty title={{ id: emptyState.title }} body={{ id: emptyState.body }} />
      </div>
    );
  };

  const columns = useMemo<TableColumn[]>(() => {
    return [
      {
        id: 'browser',
        accessorKey: 'browser',
        header: intl.formatMessage({ id: 'BROWSER' }),
        cell: (info) => {
          const token = tokens[info.row.id];
          return (
            <Cell testId={`${token.pk}-browser-column`}>
              {token.browser ?? (
                <Cell.Empty>
                  <FormattedMessage id="UNKOWN_BROWSER" />
                </Cell.Empty>
              )}
            </Cell>
          );
        },
      },
      {
        id: 'expires',
        accessorKey: 'expires',
        header: intl.formatMessage({ id: 'settings.security.expires' }),
        cell: (info) => {
          const token = tokens[info.row.id];
          return (
            <Cell testId={`${token.pk}-expires-column`}>
              {dayjs(token.expires).format('DD/MM/YYYY hh:mm A')}
            </Cell>
          );
        },
      },
    ];
  }, [tokens]);

  const rowsData = useMemo<TableRow[]>(() => {
    return list.map<TableRow>((tokenId) => {
      const token = tokens[tokenId];
      return {
        ...token,
        id: token.pk,
        suffix:
          selectedTab === 'activeSession'
            ? {
                actions: [
                  {
                    label: intl.formatMessage({
                      id: 'REMOVE_ACTIVE_SESSION',
                    }),
                    onClick: () => handleRemoveSession(tokenId),
                  },
                ],
              }
            : undefined,
      };
    });
  }, [list, tokens]);

  return (
    <div className={styles.list}>
      <ThunksTable
        identity={IDENTITY}
        options={{ columns, data: rowsData }}
        fetchObjects={fetchObjects}
        selectable={false}
        renderFooter={() => null}
        renderEmptyState={renderEmptyState}
        loadingLabel={intl.formatMessage({
          id: loadingState.message,
        })}
        testId="tokens"
      />
    </div>
  );
};

export default TokenList;
